import { type Breakpoint, Grid, type TimelineItemProps } from 'antd';
import type { AxiosResponse } from 'axios';
import { type Dispatch, type SetStateAction, createContext, useState } from 'react';
import type { ILeavePackage } from 'src/types/hr_ticket/leavePackage.type';
import type { IAdditionalWorkingTimePackage } from 'src/types/hr_ticket/additionalWorkingTimePackage.type';
import type { IAdvancePaymentTicket } from 'src/types/hr_ticket/advancePaymentTicket.type';
import type { IBusinessTripTicket } from 'src/types/hr_ticket/businessTripTicket.type';
import type { IEquipmentSupplyRequestTicket } from 'src/types/hr_ticket/equipmentSupplyRequestTicket.type';
import type { ILeaveTicket } from 'src/types/hr_ticket/leaveTicket.type';
import type { IOvertimeTicket } from 'src/types/hr_ticket/overtimeTicket.type';
import type { IPurchaseRequestTicket } from 'src/types/hr_ticket/purchaseRequestTicket.type';
import type { ITransportationTicket } from 'src/types/hr_ticket/transportationTicket.type';
import type { IAdditionalWorkingTimeTicket } from 'src/types/hr_ticket/additionalWorkingTimeTicket.type';
import type { IExpenseTrackingRequestTicket } from 'src/types/hr_ticket/expenseTrackingRequestTicket.type';
import { IResignationTicket } from 'src/types/hr_ticket/resignationTicket.type';

const { useBreakpoint } = Grid;

interface HrTicketDetailContextInterface {
  leaveTicket: AxiosResponse<ILeaveTicket, any> | undefined;
  setLeaveTicket: Dispatch<SetStateAction<AxiosResponse<ILeaveTicket, any> | undefined>>;
  leavePackage: AxiosResponse<ILeavePackage, any> | undefined;
  setLeavePackage: Dispatch<SetStateAction<AxiosResponse<ILeavePackage, any> | undefined>>;
  overtimeTicket: AxiosResponse<IOvertimeTicket, any> | undefined;
  setOvertimeTicket: Dispatch<SetStateAction<AxiosResponse<IOvertimeTicket, any> | undefined>>;

  expenseTrackingRequestTicket: AxiosResponse<IExpenseTrackingRequestTicket, any> | undefined;
  setExpenseTrackingRequestTicket: Dispatch<
    SetStateAction<AxiosResponse<IExpenseTrackingRequestTicket, any> | undefined>
  >;

  advancePaymentTicket: AxiosResponse<IAdvancePaymentTicket, any> | undefined;
  setAdvancePaymentTicket: Dispatch<SetStateAction<AxiosResponse<IAdvancePaymentTicket, any> | undefined>>;
  businessTripTicket: AxiosResponse<IBusinessTripTicket, any> | undefined;
  setBusinessTripTicket: Dispatch<SetStateAction<AxiosResponse<IBusinessTripTicket, any> | undefined>>;
  transportationTicket: AxiosResponse<ITransportationTicket, any> | undefined;
  setTransportationTicket: Dispatch<SetStateAction<AxiosResponse<ITransportationTicket, any> | undefined>>;
  purchaseRequestTicket: AxiosResponse<IPurchaseRequestTicket, any> | undefined;
  setPurchaseRequestTicket: Dispatch<SetStateAction<AxiosResponse<IPurchaseRequestTicket, any> | undefined>>;
  equipmentSupplyRequestTicket: AxiosResponse<IEquipmentSupplyRequestTicket, any> | undefined;
  setEquipmentSupplyRequestTicket: Dispatch<
    SetStateAction<AxiosResponse<IEquipmentSupplyRequestTicket, any> | undefined>
  >;
  additionalWorkingTimeTicket: AxiosResponse<IAdditionalWorkingTimeTicket, any> | undefined;
  setAdditionalWorkingTimeTicket: Dispatch<
    SetStateAction<AxiosResponse<IAdditionalWorkingTimeTicket, any> | undefined>
  >;
  additionalWorkingTimePackage: AxiosResponse<IAdditionalWorkingTimePackage, any> | undefined;
  setAdditionalWorkingTimePackage: Dispatch<
    SetStateAction<AxiosResponse<IAdditionalWorkingTimePackage, any> | undefined>
  >;
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  approvalProcessTimeLine: TimelineItemProps[] | undefined;
  setApprovalProcessTimeLine: Dispatch<SetStateAction<TimelineItemProps[] | undefined>>;
  screens: Partial<Record<Breakpoint, boolean>> | undefined;

  resignationTicket: AxiosResponse<IResignationTicket, any> | undefined;
  setResignationTicket: Dispatch<SetStateAction<AxiosResponse<IResignationTicket, any> | undefined>>;
}

const initialHrTicketDetailContext: HrTicketDetailContextInterface = {
  leaveTicket: undefined,
  setLeaveTicket: () => null,
  leavePackage: undefined,
  setLeavePackage: () => null,
  overtimeTicket: undefined,
  setOvertimeTicket: () => null,
  expenseTrackingRequestTicket: undefined,
  setExpenseTrackingRequestTicket: () => null,
  advancePaymentTicket: undefined,
  setAdvancePaymentTicket: () => null,
  businessTripTicket: undefined,
  setBusinessTripTicket: () => null,
  transportationTicket: undefined,
  setTransportationTicket: () => null,
  purchaseRequestTicket: undefined,
  setPurchaseRequestTicket: () => null,
  equipmentSupplyRequestTicket: undefined,
  setEquipmentSupplyRequestTicket: () => null,
  additionalWorkingTimeTicket: undefined,
  setAdditionalWorkingTimeTicket: () => null,
  additionalWorkingTimePackage: undefined,
  setAdditionalWorkingTimePackage: () => null,
  loading: true,
  setLoading: () => null,
  approvalProcessTimeLine: undefined,
  setApprovalProcessTimeLine: () => null,
  screens: undefined,
  resignationTicket: undefined,
  setResignationTicket: () => null
};

export const HrTicketDetailContext = createContext<HrTicketDetailContextInterface>(initialHrTicketDetailContext);

export const HrTicketDetailProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [approvalProcessTimeLine, setApprovalProcessTimeLine] = useState<TimelineItemProps[] | undefined>(undefined);
  const screens = useBreakpoint();
  const [leaveTicket, setLeaveTicket] = useState<AxiosResponse<ILeaveTicket, any> | undefined>(
    initialHrTicketDetailContext.leaveTicket
  );
  const [leavePackage, setLeavePackage] = useState<AxiosResponse<ILeavePackage, any> | undefined>(
    initialHrTicketDetailContext.leavePackage
  );
  const [overtimeTicket, setOvertimeTicket] = useState<AxiosResponse<IOvertimeTicket, any> | undefined>(
    initialHrTicketDetailContext.overtimeTicket
  );
  const [expenseTrackingRequestTicket, setExpenseTrackingRequestTicket] = useState<
    AxiosResponse<IExpenseTrackingRequestTicket, any> | undefined
  >(initialHrTicketDetailContext.expenseTrackingRequestTicket);
  const [advancePaymentTicket, setAdvancePaymentTicket] = useState<
    AxiosResponse<IAdvancePaymentTicket, any> | undefined
  >(initialHrTicketDetailContext.advancePaymentTicket);
  const [businessTripTicket, setBusinessTripTicket] = useState<AxiosResponse<IBusinessTripTicket, any> | undefined>(
    initialHrTicketDetailContext.businessTripTicket
  );
  const [transportationTicket, setTransportationTicket] = useState<
    AxiosResponse<ITransportationTicket, any> | undefined
  >(initialHrTicketDetailContext.transportationTicket);
  const [purchaseRequestTicket, setPurchaseRequestTicket] = useState<
    AxiosResponse<IPurchaseRequestTicket, any> | undefined
  >(initialHrTicketDetailContext.purchaseRequestTicket);
  const [equipmentSupplyRequestTicket, setEquipmentSupplyRequestTicket] = useState<
    AxiosResponse<IEquipmentSupplyRequestTicket, any> | undefined
  >(initialHrTicketDetailContext.equipmentSupplyRequestTicket);
  const [additionalWorkingTimeTicket, setAdditionalWorkingTimeTicket] = useState<
    AxiosResponse<IAdditionalWorkingTimeTicket, any> | undefined
  >(initialHrTicketDetailContext.additionalWorkingTimeTicket);
  const [additionalWorkingTimePackage, setAdditionalWorkingTimePackage] = useState<
    AxiosResponse<IAdditionalWorkingTimePackage, any> | undefined
  >(initialHrTicketDetailContext.additionalWorkingTimePackage);
  const [resignationTicket, setResignationTicket] = useState<AxiosResponse<IResignationTicket, any> | undefined>(
    initialHrTicketDetailContext.resignationTicket
  );
  return (
    <HrTicketDetailContext.Provider
      value={{
        leaveTicket,
        setLeaveTicket,
        leavePackage,
        setLeavePackage,
        overtimeTicket,
        setOvertimeTicket,
        expenseTrackingRequestTicket,
        setExpenseTrackingRequestTicket,
        advancePaymentTicket,
        setAdvancePaymentTicket,
        businessTripTicket,
        setBusinessTripTicket,
        transportationTicket,
        setTransportationTicket,
        purchaseRequestTicket,
        setPurchaseRequestTicket,
        equipmentSupplyRequestTicket,
        setEquipmentSupplyRequestTicket,
        additionalWorkingTimeTicket,
        setAdditionalWorkingTimeTicket,
        additionalWorkingTimePackage,
        setAdditionalWorkingTimePackage,
        loading,
        setLoading,
        approvalProcessTimeLine,
        setApprovalProcessTimeLine,
        screens,
        resignationTicket,
        setResignationTicket
      }}
    >
      {children}
    </HrTicketDetailContext.Provider>
  );
};
